import {
  createContext,
  useContext,
  useState,
  type FC,
  type ReactNode,
} from 'react';

import { isAxiosError } from 'axios';

import ConfirmationPopup from './confirmationPopup';

interface PopupConfig {
  title: string;
  content: ReactNode;
  confirmText?: string;
  cancelText?: string;
  confirmColor?: 'primary' | 'secondary' | 'error';
  onConfirm?: () => void | Promise<void>;
}

interface PopupContextType {
  showPopup: (_config: PopupConfig) => Promise<boolean>;
}

const PopupContext = createContext<PopupContextType | undefined>(undefined);

export const PopupProvider: FC<{ children: ReactNode }> = ({ children }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [config, setConfig] = useState<PopupConfig | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [statusMessage, setStatusMessage] = useState<string>('');
  const [resolvePromise, setResolvePromise] = useState<
    ((_value: boolean) => void) | null
  >(null);

  const showPopup = (popupConfig: PopupConfig): Promise<boolean> => {
    return new Promise((resolve) => {
      setConfig(popupConfig);
      setIsOpen(true);
      setStatusMessage('');
      setResolvePromise(() => resolve);
    });
  };

  const handleConfirm = async () => {
    if (config?.onConfirm) {
      setIsLoading(true);
      setStatusMessage('');
      try {
        setIsOpen(false);
        await config.onConfirm();
        if (resolvePromise) {
          resolvePromise(true);
          setResolvePromise(null);
        }
      } catch (error) {
        setStatusMessage(
          isAxiosError(error)
            ? (error.response?.data as string)
            : error instanceof Error
              ? error.message
              : 'An error occurred',
        );
      }
      setIsLoading(false);
    } else {
      setIsOpen(false);
      if (resolvePromise) {
        resolvePromise(true);
        setResolvePromise(null);
      }
    }
  };

  const handleCancel = () => {
    setIsOpen(false);
    setStatusMessage('');
    if (resolvePromise) {
      resolvePromise(false);
      setResolvePromise(null);
    }
  };

  return (
    <PopupContext.Provider value={{ showPopup }}>
      {children}
      {config && (
        <ConfirmationPopup
          isOpen={isOpen}
          title={config.title}
          confirmText={config.confirmText}
          cancelText={config.cancelText}
          confirmColor={config.confirmColor}
          isLoading={isLoading}
          statusMessage={statusMessage}
          statusType="error"
          onConfirm={() => void handleConfirm()}
          onCancel={handleCancel}
        >
          {config.content}
        </ConfirmationPopup>
      )}
    </PopupContext.Provider>
  );
};

export const usePopup = (): PopupContextType => {
  const context = useContext(PopupContext);
  if (!context) {
    throw new Error('usePopup must be used within a PopupProvider');
  }
  return context;
};
